import { Fragment, useEffect, useRef, useState } from 'react'
import {
  isStopPhysicalCheck,
  releasableStopIds,
  requiredStopChecks,
  stopsReleaseFailure,
  useModules,
  useReleaseStops,
  useSafetyState,
  useStops,
  type StopItem,
  type StopsReleaseResult,
} from '@ctcells/api'
import type { ModuleSnapshot } from '@ctcells/contract'
import { dataSourceFor } from '@ctcells/core'
import { enumLabel, t, type MessageKey } from '@ctcells/i18n'

import { Button } from '../atoms/Button.tsx'
import { Checkbox } from '../atoms/Checkbox.tsx'
import { Field } from '../atoms/Field.tsx'
import { TextInput } from '../atoms/TextInput.tsx'
import { useSubmitValidation } from '../atoms/useSubmitValidation.ts'
import { gate, type ActionGate, type ScreenAccess } from '../screens/access.ts'
import { FormSection, ModalFooter } from '../screens/formSections.tsx'
import { sequenceDictionaryOfModuleType } from '../screens/monitoring/latestStepModel.ts'
import { StopsAbortedDoorRecover } from './StopsAbortedDoorRecover.tsx'
import { stopItemText } from './stopsRelease.ts'
import './batchHalt.css'

export interface StopsReleasePanelProps {
  access: ScreenAccess
  hideTitle?: boolean
  /** 200도 부분 실패일 수 있다. 호출자는 자동 재개·자동 닫기를 하지 않는다. */
  onReleased?: (result: StopsReleaseResult) => void
}

interface Confirmation {
  snapshot: string | null
  items: readonly StopItem[]
  checked: readonly string[]
  extraChecks: readonly string[]
  reason: string
}

/**
 * 해제에 실패한 줄 아래 붙이는 «그 모듈의 지금 상태» 한 줄 — 🔴 서버 스냅숏(`GET /modules` 캐시) 값을 사전 말로 옮기기만 한다.
 * 조치 문장을 짓지 않는다. 사전에 없는 값은 원값(`enumLabel` 규약). 스냅숏이 `stale` 이면 「마지막 값」으로 적는다.
 */
function moduleNowText(module: ModuleSnapshot): string {
  const execution = module.current_execution
  const sequence = execution?.sequence
  const dictionary = sequenceDictionaryOfModuleType(module.module_type)
  const sequenceName =
    sequence === null || sequence === undefined || sequence === ''
      ? undefined
      : dictionary === undefined
        ? sequence
        : enumLabel(dictionary, sequence)
  const holdKind = module.hold?.kind
  const parts = [
    enumLabel('enum.moduleState', module.state),
    sequenceName === undefined
      ? undefined
      : typeof execution?.step === 'number' && typeof execution.total === 'number'
        ? t('stopsRelease.moduleExecution', {
            sequence: sequenceName,
            step: execution.step,
            total: execution.total,
          })
        : sequenceName,
    holdKind === null || holdKind === undefined || holdKind === 'none'
      ? undefined
      : t('monitoring.hold', { kind: enumLabel('enum.holdKind', holdKind) }),
  ].filter((part) => part !== undefined)
  const state = parts.join(' · ')
  return t('stopsRelease.moduleNow', {
    module: module.module_id,
    state: module.stale ? t('stale.lastValue', { value: state }) : state,
  })
}

/** 모든 역할이 읽는 시스템 전체 목록. 쓰기만 control·제어락·연결로 제한한다. */
export function StopsReleasePanel({
  access,
  onReleased,
  hideTitle = false,
}: StopsReleasePanelProps) {
  const stops = useStops()
  const release = useReleaseStops()
  // 결과 줄 → 모듈은 보낸 정지 항목의 `target`·`kind` 로만 잇는다(said 문자열을 읽지 않는다). 새 조회 없이 같은 캐시를 읽는다.
  const modules = useModules({ skip: dataSourceFor('modules') !== 'live' })
  // 전체 안전 정지 줄의 글(`stopItemText`) — 작업 줄과 같은 캐시(`keys.safety.state`)를 읽는다. 새 폴링 없음
  const safety = useSafetyState({ skip: dataSourceFor('safety') !== 'live' })
  const [sentItems, setSentItems] = useState<
    ReadonlyMap<string, Pick<StopItem, 'target' | 'kind'>>
  >(() => new Map())
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const validation = useSubmitValidation((value: string) => ({
    reason: value.trim() === '' ? t('validation.reasonRequired') : undefined,
  }))
  const reasonField = validation.field('reason')
  const sending = useRef(false)
  const { refetch } = stops
  // 팝업을 열 때 한 번 읽고 이후 기존 스트림 무효화로 갱신한다.
  useEffect(() => {
    void refetch()
  }, [refetch])

  const items = stops.data?.items
  const visibleItems = items?.filter(
    (item) => !item.parent || !items.some((parent) => parent.id === item.parent),
  )
  const topLevelCount = items?.filter((item) => !item.parent).length
  const snapshot = JSON.stringify(items)
  const ids = releasableStopIds(items ?? [])
  const changed = confirmation !== null && confirmation.snapshot !== snapshot
  const checks =
    confirmation === null
      ? []
      : [
          ...new Set([
            ...requiredStopChecks(confirmation.items, releasableStopIds(confirmation.items)),
            ...confirmation.extraChecks,
          ]),
        ]
  const writeGate = gate(access, { perm: 'control', needsLock: true })
  const openGate: ActionGate = writeGate.disabled
    ? writeGate
    : release.isPending
      ? { disabled: true, disabledReason: t('stopsRelease.pending') }
      : stops.isFetching
        ? { disabled: true, disabledReason: t('pfStops.loading') }
        : stops.isError || items === undefined
          ? { disabled: true, disabledReason: stops.error?.message ?? t('pfStops.loading') }
          : ids.length === 0
            ? { disabled: true, disabledReason: t('pfStops.none') }
            : { disabled: false }
  const submitGate: ActionGate = openGate.disabled
    ? openGate
    : changed
      ? { disabled: true, disabledReason: t('stopsRelease.changed') }
      : checks.some((check) => !isStopPhysicalCheck(check))
        ? { disabled: true, disabledReason: t('stopsRelease.unknownCheck') }
        : checks.some((check) => !confirmation?.checked.includes(check))
          ? { disabled: true, disabledReason: t('reason.physicalChecksRequired') }
          : { disabled: false }

  const begin = () => {
    if (openGate.disabled || items === undefined) return
    release.reset()
    validation.reset()
    setNotice(null)
    setConfirmation((current) => ({
      snapshot,
      items,
      checked: [],
      extraChecks: [],
      reason: current?.reason ?? '',
    }))
  }
  const submit = async () => {
    if (submitGate.disabled || confirmation === null || sending.current) return
    if (
      !validation.validate(
        confirmation.reason,
        document.getElementById(reasonField.inputProps.id)?.parentElement ?? null,
      )
    )
      return
    sending.current = true
    setNotice(null)
    setSentItems(
      new Map(
        confirmation.items.map((item) => [item.id, { target: item.target, kind: item.kind }]),
      ),
    )
    try {
      const result = await release.mutateAsync({
        ids: releasableStopIds(confirmation.items),
        reason: confirmation.reason.trim(),
        physicalChecked: false,
        physicalChecks: checks.filter(isStopPhysicalCheck),
      })
      setConfirmation(null)
      onReleased?.(result)
    } catch (error) {
      const failure = stopsReleaseFailure(error)
      if (failure.kind === 'changed') {
        // 같은 목록이 돌아와도 409 뒤에는 현장 확인을 새로 받아야 한다.
        setConfirmation((current) => (current === null ? null : { ...current, snapshot: null }))
        await refetch()
      } else if (failure.kind === 'physical') {
        setNotice(t('stopsRelease.physical'))
        setConfirmation((current) =>
          current === null
            ? null
            : {
                ...current,
                checked: [],
                extraChecks: failure.checks,
              },
        )
      }
    } finally {
      sending.current = false
    }
  }

  return (
    <section className="stops-release" aria-label={t('stopsRelease.title')}>
      <FormSection wide>
        {hideTitle ? null : <h3>{t('stopsRelease.title')}</h3>}
        <p className="small muted">
          {topLevelCount === undefined
            ? t('stopsRelease.global')
            : t('stopsRelease.count', { count: topLevelCount })}
        </p>
        {stops.isError ? (
          <p className="vissue" role="alert">
            {stops.error.message}
          </p>
        ) : null}
        {stops.isPending ? <p role="status">{t('pfStops.loading')}</p> : null}
        {items?.length === 0 && !stops.isError ? (
          <p className="small">{t('stopsRelease.empty')}</p>
        ) : null}
        {items !== undefined && items.length > 0 ? (
          <ul className="stops-release__list">
            {visibleItems?.map((item) => {
              const children = items.filter((child) => child.parent === item.id)
              return (
                <Fragment key={item.id}>
                  <li>
                    {stopItemText(item, safety.data)}
                    {!item.releasable ? (
                      <span className="small muted"> · {t('stopsRelease.humanFirst')}</span>
                    ) : item.parent ? (
                      <span className="small muted"> · {t('stopsRelease.included')}</span>
                    ) : null}
                  </li>
                  {children.length > 0 ? (
                    <li className="small muted">
                      {t('stopsRelease.children', {
                        count: children.length,
                        targets: children.map((child) => child.target).join(' · '),
                      })}
                    </li>
                  ) : null}
                </Fragment>
              )
            })}
          </ul>
        ) : null}
        {notice !== null ? <p role="alert">{notice}</p> : null}
        {release.isError ? (
          <p className="vissue" role="alert">
            {release.error.message}
          </p>
        ) : null}
        {changed ? <p role="alert">{t('stopsRelease.changed')}</p> : null}
        {release.isPending ? <p role="status">{t('stopsRelease.pending')}</p> : null}
        {release.data !== undefined ? (
          <section aria-label={t('stopsRelease.result')} aria-live="polite">
            <h4>{t('stopsRelease.result')}</h4>
            <ul className="stops-release__list">
              {release.data.items.map((item) => {
                const sent = item.done ? undefined : sentItems.get(item.id)
                const target = sent?.target
                const snapshot =
                  target === undefined
                    ? undefined
                    : modules.data?.items.find((row) => row.module_id === target)
                return (
                  <li key={item.id} style={{ overflowWrap: 'anywhere' }}>
                    {t(item.done ? 'stopsRelease.done' : 'stopsRelease.failed')} · {item.said}
                    {item.error_code ? ` (${item.error_code})` : null}
                    {snapshot === undefined ? null : (
                      <div
                        className="small muted"
                        style={{
                          paddingInlineStart: 'var(--space-md)',
                          marginTop: 'var(--space-2xs)',
                        }}
                      >
                        {moduleNowText(snapshot)}
                      </div>
                    )}
                    {target === undefined ? null : (
                      <StopsAbortedDoorRecover
                        access={access}
                        kind={sent?.kind}
                        done={item.done}
                        errorCode={item.error_code}
                        moduleId={target}
                        module={snapshot}
                        onFinished={refetch}
                      />
                    )}
                  </li>
                )
              })}
            </ul>
            <p className="small muted">{t('stopsRelease.after')}</p>
          </section>
        ) : null}
        {confirmation !== null && !changed ? (
          <>
            <p className="small">{t('pfStops.releasable')}</p>
            <ul className="stops-release__list">
              {confirmation.items
                .filter((item) => releasableStopIds(confirmation.items).includes(item.id))
                .map((item) => (
                  <li key={item.id} style={{ overflowWrap: 'anywhere' }}>
                    {stopItemText(item, safety.data)}
                  </li>
                ))}
            </ul>
            {checks.map((check) => (
              <Checkbox
                key={check}
                label={
                  isStopPhysicalCheck(check)
                    ? t(`stopsRelease.check.${check}` as MessageKey)
                    : check
                }
                checked={confirmation.checked.includes(check)}
                {...(release.isPending
                  ? { disabled: true as const, disabledReason: t('stopsRelease.pending') }
                  : {})}
                onChange={(on) =>
                  setConfirmation((current) =>
                    current === null
                      ? null
                      : {
                          ...current,
                          checked: on
                            ? [...current.checked, check]
                            : current.checked.filter((value) => value !== check),
                        },
                  )
                }
              />
            ))}
            <Field {...reasonField.fieldProps} label={t('field.reason')} required>
              <TextInput
                {...reasonField.inputProps}
                value={confirmation.reason}
                wide
                readOnly={release.isPending}
                onChange={(reason) => {
                  validation.change('reason', reason)
                  setConfirmation((current) => (current === null ? null : { ...current, reason }))
                }}
              />
            </Field>
          </>
        ) : null}
      </FormSection>
      <ModalFooter>
        {changed ? (
          <Button
            aria-label={t('stopsRelease.reconfirm')}
            size="pill"
            {...openGate}
            onClick={begin}
          >
            {t('stopsRelease.reconfirm')}
          </Button>
        ) : confirmation !== null ? (
          <>
            <Button
              aria-label={t('action.cancel')}
              size="pill"
              onClick={() => setConfirmation(null)}
              {...(release.isPending
                ? { disabled: true as const, disabledReason: t('stopsRelease.pending') }
                : {})}
            >
              {t('action.cancel')}
            </Button>
            <Button
              aria-label={t('stopsRelease.confirm')}
              size="pill"
              variant="primary"
              {...submitGate}
              onClick={() => {
                void submit()
              }}
            >
              {t('stopsRelease.confirm')}
            </Button>
          </>
        ) : (
          <Button
            aria-label={t('stopsRelease.action')}
            size="pill"
            variant="primary"
            {...openGate}
            onClick={begin}
          >
            {t('stopsRelease.action')}
          </Button>
        )}
      </ModalFooter>
    </section>
  )
}
